import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import React, { useState } from "react";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import { DASH_OPTIONS } from "../charts/dataset-line-chart/line-editor/LineEditor";

export interface CurrentLineConfig {
  color?: string;
  width?: number;
  dashType?: string;
  range?: {
    min?: number;
    max?: number;
  };
}

enum NumberInputEditorType {
  MIN = "min",
  MAX = "max",
  WIDTH = "width",
}

const defaultConfig = {
  color: undefined,
  width: undefined,
  dashType: undefined,
  range: {
    min: undefined,
    max: undefined,
  },
};

interface DatasetLineConfigOptionsProps {
  currentLineConfig?: CurrentLineConfig;
  identifier: string;
  setCurrentLineConfig: (identifier: string, currentLineConfig: CurrentLineConfig) => void;
}

const DatasetLineConfigOptions = (props: DatasetLineConfigOptionsProps) => {
  const [currentConfig, setCurrentConfig] = useState(props.currentLineConfig || defaultConfig);

  const handleMinChange = (e: { value: number | null }) => {
    if (e.value && currentConfig.range?.max && e.value >= currentConfig.range.max) {
      easasAlert({
        summary: "Error",
        detail: "Min value must be less than max value",
        severity: "error",
        life: 3000,
      });
      return;
    }
    const newValue = e.value !== null ? e.value : undefined;
    setCurrentConfig({
      ...currentConfig,
      range: { ...currentConfig.range, min: newValue },
    });
  };

  const handleMaxChange = (e: { value: number | null }) => {
    if (e.value && currentConfig.range?.min && e.value <= currentConfig.range.min) {
      easasAlert({
        summary: "Error",
        detail: "Max value must be greater than min value",
        severity: "error",
        life: 3000,
      });
      return;
    }
    const newValue = e.value !== null ? e.value : undefined;
    setCurrentConfig({
      ...currentConfig,
      range: { ...currentConfig.range, max: newValue },
    });
  };

  const handleEditorCallback = (newConfig: CurrentLineConfig) => {
    setCurrentConfig({
      ...currentConfig,
      ...newConfig,
    });
  };

  const lineColorEditor = () => {
    const value = currentConfig.color ?? "#FFFFFF";
    return (
      <div className="flex gap-1 align-items-center">
        <ColorPicker
          value={value}
          onValueChange={e => {
            const color = e.target.value;
            handleEditorCallback({ color });
          }}
        />
        <i
          className="pi pi-refresh ml-2 cursor-pointer"
          onClick={() => handleEditorCallback({ color: "#000000" })}
        />
      </div>
    );
  };

  const customDropdownItem = (option: any) => {
    return option ? (
      <div>
        <svg height="15" width="100">
          <line
            x1="0"
            y1="50%"
            x2="100%"
            y2="50%"
            stroke="black"
            strokeWidth="2"
            strokeDasharray={option.strokeDasharray}
          />
        </svg>
      </div>
    ) : (
      <div>Select a dash</div>
    );
  };

  const dashEditor = () => {
    return (
      <Dropdown
        className="w-full"
        value={currentConfig.dashType}
        placeholder="Select a dash"
        options={DASH_OPTIONS}
        onChange={e => handleEditorCallback({ dashType: e.value })}
        itemTemplate={option => customDropdownItem(option)}
      />
    );
  };

  const numberEditor = (field: NumberInputEditorType) => {
    return (
      <InputNumber
        inputStyle={{ width: "100%" }}
        value={field === NumberInputEditorType.WIDTH ? currentConfig.width : currentConfig.range?.[field]}
        onValueChange={e => {
          if (field === NumberInputEditorType.MIN) {
            handleMinChange(e);
          } else if (field === NumberInputEditorType.MAX) {
            handleMaxChange(e);
          } else if (field === NumberInputEditorType.WIDTH) {
            handleEditorCallback({ width: e.value || undefined });
          }
        }}
      />
    );
  };

  const colorBodyTemplate = () => {
    let currentColor = currentConfig.color;

    if (!currentColor) {
      currentColor = "#FFFFFF";
    }
    return (
      <div
        style={{
          width: "25px",
          height: "25px",
          backgroundColor: currentColor?.startsWith("#") ? currentColor : `#${currentColor}`,
          borderRadius: "5px",
          border: "1px solid #c8c8c8",
        }}
      />
    );
  };

  const onRowEditComplete = () => {
    props.setCurrentLineConfig(props.identifier, currentConfig);
  };

  return (
    <DataTable value={[currentConfig]} editMode="row" onRowEditComplete={onRowEditComplete}>
      <Column field="color" header="Color" editor={lineColorEditor} body={colorBodyTemplate} />
      <Column
        field="width"
        header="Width"
        editor={() => numberEditor(NumberInputEditorType.WIDTH)}
        body={data => data.width || "Default"}
      />
      <Column
        field="dashType"
        header="Dash Type"
        editor={dashEditor}
        body={data => data.dashType || "Solid"}
      />
      <Column
        field="range.min"
        header="Min"
        editor={() => numberEditor(NumberInputEditorType.MIN)}
        body={data => data.range.min || "Default"}
      />
      <Column
        field="range.max"
        header="Max"
        editor={() => numberEditor(NumberInputEditorType.MAX)}
        body={data => data.range.max || "Default"}
      />
      <Column rowEditor bodyStyle={{ textAlign: "end" }} />
    </DataTable>
  );
};

export default DatasetLineConfigOptions;
